import dayjs from 'dayjs';
import {getEnvMode, isDevelopment} from '@/utils/env';
import {isLocalAppHost} from '@/utils/appHost';
import type {BookingFilter} from '@/types/booking';

/**
 * Builds a query string from an object of parameters, with optional ignoring of empty values and handling of nested objects.
 * @param params - The parameters object
 * @param removeEmpty - Whether to remove empty values
 * @returns The query string
 */
export function createUrlQueryString(params: Record<string, any>, removeEmpty = true): string {
    if (params === undefined || typeof params !== 'object') {
        throw new Error('Params must be an object');
    }

    if (removeEmpty) {
        params = Object.entries(params)
            .filter(([, value]) => {
                // Filter out null, undefined, empty strings, and empty arrays
                if (value == null || value === '') {
                    return false;
                }
                return !(Array.isArray(value) && value.length === 0);
            })
            .reduce((acc, [key, value]) => ({...acc, [key]: value}), {});
    }

    const buildNestedQueryString = (obj: Record<string, any>, parentKey = ''): string => {
        return Object.entries(obj)
            .map(([key, value]) => {
                const fullKey = parentKey ? `${parentKey}[${key}]` : key;

                if (value === null || value === undefined) {
                    return `${fullKey}=`;
                } else if (typeof value === 'object' && !Array.isArray(value)) {
                    return buildNestedQueryString(value, fullKey);
                } else if (Array.isArray(value)) {

                    return value.length
                        ? value.map((arrayItem) => `${fullKey}[]=${encodeURIComponent(String(arrayItem))}`).join('&')
                        : `${fullKey}=`; // Handle empty arrays
                } else if (
                    typeof value === 'string' ||
                    typeof value === 'number' ||
                    typeof value === 'boolean'
                ) {
                    return `${fullKey}=${encodeURIComponent(value)}`;
                } else {
                    throw new Error('Invalid value type');
                }
            })
            .join('&');
    }

    const queryString = buildNestedQueryString(params);
    return queryString ? `?${queryString}` : '';
}


/**
 * Builds a query string from an object of parameters, with optional ignoring of empty values and handling of nested objects.
 * @param data - The parameters object
 * @param removeEmpty - Whether to remove empty values
 * @returns The query string
 */
export const createOrderUrlQueryString = (data: {
    [x: string]: any;
    page?: number;
    itemsPerPage?: number;
    includeAbandoned?: boolean | undefined;
    onlyAbandoned?: boolean | undefined;
    withOrderProperties?: boolean | undefined;
    withRenterProperties?: boolean | undefined;
    searchTerm?: string | undefined;
    searchTopic?: string | undefined;
    exporting?: boolean | undefined;
    providers?: string[];
    statuses?: string | undefined;
    filter?: BookingFilter | undefined;
    sortBy?: string[] | undefined;
    filterQuery?: string | undefined;
    sortDesc?: string | boolean | boolean[];
    document_type?: string | undefined;
} | undefined, removeEmpty = false) => {
    if (data === undefined) {
        return '';
    }
    // Check if data is an object
    if (typeof data !== 'object') {
        throw new Error('Data must be an object');
    }

    if (removeEmpty) {
        Object.keys(data).forEach((key) => {
            if (data[key] === '' || data[key] === null || data[key] === undefined) {
                delete data[key];
            }
        });
    }

    let queryString = '?';

    Object.keys(data).forEach((key) => {
        if (typeof data[key] === 'object') {
            Object.keys(data[key]).forEach((subKey) => {
                queryString += `${key}[${subKey}]=${data[key][subKey]}&`;
            });
        } else {
            queryString += `${key}=${data[key]}&`;
        }
    });

    // Remove the leading ? if there are no params
    if (queryString === '?') {
        queryString = '';
    }

    // remove last & if any
    if (queryString[queryString.length - 1] === '&') {
        queryString = queryString.slice(0, -1);
    }
    return queryString;
};


export const formatQueryStringDate = (date: string) => {
    if (!date) throw new Error('Date is required');

    const dateWithHyphen = (input: string): string => {
        const parts = input.split('-');
        let year = parts[0];
        if (year.length === 2) year = `20${year}`;

        const newDate = dayjs(`${year}-${parts[1]}-${parts[2]}`);
        if (newDate.isValid()) {
            return newDate.format('YYYY-MM-DD');
        }

        throw new Error('Invalid date format');
    };

    const dateWithoutHyphen = (input: string, format: string): string => {
        let year: string, month: string, day: string;

        if (format === 'YYYYMMDD') {
            year = input.substring(0, 4);
            month = input.substring(4, 6);
            day = input.substring(6, 8);
        } else if (format === 'YYMMDD') {
            year = `20${input.substring(0, 2)}`;
            month = input.substring(2, 4);
            day = input.substring(4, 6);
        } else {
            throw new Error('Invalid date format');
        }

        const newDate = dayjs(`${year}-${month}-${day}`);
        if (newDate.isValid()) {
            return newDate.format('YYYY-MM-DD');
        }

        throw new Error('Invalid date format');
    };

    if (date.includes('-')) {
        return dateWithHyphen(date);
    }

    if (date.length === 8) {
        return dateWithoutHyphen(date, 'YYYYMMDD');
    }

    if (date.length === 6) {
        return dateWithoutHyphen(date, 'YYMMDD');
    }

    throw new Error('Invalid date format');
}

export const formatQueryStringTime = (time: string | undefined): string => {
    if (!time) return '10:00:00';

    if (time.length !== 4) {
        throw new Error('Invalid time format. Expected HHMM format.');
    }

    const hours = time.slice(0, 2);
    const minutes = time.slice(2, 4);

    return `${hours}:${minutes}:00`;
};


const baseUrls: Record<'production' | 'development', string> = {
    production: 'https://tenant.fleetwire.io/',
    development: 'http://fleetwire.test/',
};

/**
 * Ensure a custom domain string is an absolute origin URL (with a scheme).
 * Prevents relative resolution when the value is stored without https:// (e.g. "checkout.example.com").
 */
export const toAbsoluteOriginUrl = (domain: string): string => {
    if (!domain || typeof domain !== 'string') {
        return domain;
    }
    const trimmed = domain.trim();
    if (!trimmed) {
        return domain;
    }
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed.replace(/\/+$/, '');
    }
    return `https://${trimmed.replace(/^\/+|\/+$/g, '')}`;
};

/**
 * Get the base URL based on the environment mode.
 * @param envOverride Optional override for the environment mode.
 * @returns The base URL for the current or specified environment.
 */
export const getBaseUrl = (envOverride?: { MODE?: string } | null): string => {
    const env = getEnvMode(envOverride || import.meta.env);
    return baseUrls[env] || baseUrls.development;
};

interface StoreUrlCompany {
    store_url?: string | null;
    platform_store_url?: string | null;
    slug?: string | null;
}

/**
 * Canonical public storefront base URL for a company — the single frontend source of
 * truth. Prefers the server-resolved `store_url` (custom domain > store domain > app
 * domain; see Company::store_url), falling back to the legacy pattern only for older
 * API payloads that predate the field. Returns null when the company has no slug.
 *
 * Every place that links to a tenant's public store must use this (optionally with a
 * path) so the store domain is honored in exactly one place — not re-hardcoded per view.
 */
export const companyStoreBaseUrl = (company: StoreUrlCompany | null | undefined): string | null => {
    if (!company) {
        return null;
    }

    // Local dev: keep store links on the local .test host so they open without hand-
    // editing the URL each time. The subdomain form composes with paths exactly like
    // production ({slug}.fleetwire.shop).
    //
    // Keyed off the host we are actually served from as well as the build MODE:
    // npm run build:local compiles in production mode, so a MODE check alone sent
    // every dashboard "Open" to the live storefront whenever local assets were built
    // rather than served by the Vite dev server. Production is never served from
    // .test, so this cannot leak into a deployed build.
    if (company.slug && (isDevelopment() || isLocalAppHost())) {
        return `https://${company.slug}.fleetwire.test`;
    }

    if (company.store_url) {
        return company.store_url.replace(/\/+$/, '');
    }

    return company.slug ? `https://${company.slug}.fleetwire.io` : null;
};

/**
 * Canonical store URL with an optional path appended (e.g. '/rental').
 */
export const companyStoreUrl = (company: StoreUrlCompany | null | undefined, path = ''): string | null => {
    const base = companyStoreBaseUrl(company);

    if (!base) {
        return null;
    }

    if (!path) {
        return base;
    }

    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};
/**
 * The store as the backoffice "Preview & edit" page frames it
 * (docs/storefront-preview-and-edit.md): always Fleetwire's own host, never the
 * custom domain, so the preview does not depend on a tenant's DNS or cert, and
 * always in preview mode (`?preview=1`: no host analytics, bookings refused).
 *
 * `reloadKey` busts caches when the preview reloads after a save.
 */
export const companyPreviewStoreUrl = (
    company: StoreUrlCompany | null | undefined,
    path = '',
    reloadKey?: number | string,
): string | null => {
    if (!company?.slug) {
        return null;
    }

    const base = isDevelopment() || isLocalAppHost()
        ? `https://${company.slug}.fleetwire.test`
        : (company.platform_store_url || `https://${company.slug}.fleetwire.io`).replace(/\/+$/, '');

    const query = new URLSearchParams({preview: '1'});
    if (reloadKey !== undefined) {
        query.set('_preview', String(reloadKey));
    }

    const normalizedPath = path ? (path.startsWith('/') ? path : `/${path}`) : '';

    return `${base}${normalizedPath}?${query.toString()}`;
};

/**
 * Single trimmed value for a route query parameter.
 *
 * Vue Router types a query value as string | string[] | null, and a repeated
 * parameter (?search=a&search=b) arrives as an array — reading it as a string
 * silently seeds a search box with "a,b". Returns '' when there is nothing
 * usable to read.
 */
export const readQueryParam = (
    value: string | (string | null)[] | null | undefined,
): string => {
    const first = Array.isArray(value) ? value[0] : value;

    return typeof first === 'string' ? first.trim() : '';
};
